import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, Avatar, IconButton, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import TenantDetailsDialog from './TenantDetailsDialog';
import TenantCreateDialog from './TenantCreateDialog';
import TenantEditDialog from './TenantEditDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface TenantFile {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  uploadDate: string;
}

interface Tenant {
  id: string;
  fullName: string;
  phoneNumbers: string[];
  emails: string[];
  nationality: string | null;
  identificationType: string;
  identificationTypeId: string;
  username?: string;
  active: boolean;
  files?: TenantFile[];
}

const TenantsListing: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- View Tenant Details ----------
  const handleViewClick = (tenant: Tenant) => {
    // Navigate to tenant details page
    window.location.href = `/tenants/${tenant.id}`;
  };

  // ---------- Edit Tenant ----------
  const handleEditClick = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setEditDialogOpen(true);
  };

  // ---------- Add Tenant ----------
  const handleAddTenant = () => {
    setCreateDialogOpen(true);
  };

  // ---------- Handle Create Success ----------
  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
  };

  // ---------- Handle Edit Success ----------
  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setSelectedTenant(null);
    setRefreshKey((prev) => prev + 1);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (tenant: Tenant) => {
    setToggleLoading(tenant.id);

    try {
      const endpoint = tenant.active
        ? `api/Tenant/deactivate?id=${tenant.id}`
        : `api/Tenant/activate?id=${tenant.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `Tenant ${tenant.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        // Try to get error details from response
        let errorMessage = `Failed to ${tenant.active ? 'deactivate' : 'activate'} tenant`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to ${tenant.active ? 'deactivate' : 'activate'} tenant: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error toggling tenant status:', error);
      const errorMessage = error instanceof Error ? error.message : `Failed to update tenant status. Please try again.`;
      dispatch(
        showAlert({
          message: errorMessage,
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'fullName',
      headerName: 'Tenant',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const fullName = params.row.fullName || 'Unknown';
        const primaryEmail = params.row.emails && params.row.emails.length > 0 ? params.row.emails[0] : '';

        // Generate initials from full name
        const initials = fullName.split(' ')
          .map(name => name.charAt(0))
          .join('')
          .toUpperCase()
          .substring(0, 2);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem'
              }}
            >
              {initials || <PersonIcon fontSize="small" />}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {fullName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {primaryEmail || 'No email'}
              </Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'username',
      headerName: 'Username',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || (
            <span style={{ fontStyle: 'italic', color: 'gray' }}>
              Not specified
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'phoneNumbers',
      headerName: 'Phone Numbers',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const phones = params.row.phoneNumbers || [];
        return (
          <Typography variant="body2">
            {phones.length > 0 ? (
              <>
                {phones[0]}
                {phones.length > 1 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    +{phones.length - 1} more
                  </Typography>
                )}
              </>
            ) : (
              <span style={{ fontStyle: 'italic', color: 'gray' }}>
                No phone
              </span>
            )}
          </Typography>
        );
      }
    },
    {
      field: 'emails',
      headerName: 'Emails',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const emails = params.row.emails || [];
        return (
          <Typography variant="body2">
            {emails.length > 0 ? (
              <>
                {emails[0]}
                {emails.length > 1 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    +{emails.length - 1} more
                  </Typography>
                )}
              </>
            ) : (
              <span style={{ fontStyle: 'italic', color: 'gray' }}>
                No email
              </span>
            )}
          </Typography>
        );
      }
    },
    {
      field: 'nationality',
      headerName: 'Nationality',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || (
            <span style={{ fontStyle: 'italic', color: 'gray' }}>
              Not specified
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'identificationType',
      headerName: 'ID Type',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || (
            <span style={{ fontStyle: 'italic', color: 'gray' }}>
              Not specified
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Tenant">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="secondary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewClick(params.row)}
              color="primary"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add Tenant',
      icon: <AddIcon />,
      onClick: handleAddTenant,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Tenants Function ----------
  const fetchTenants = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchTenants called with params:', params);
      try {
        const response = await DataService.get('api/Tenant/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawTenants: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawTenants = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawTenants = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const tenants: Tenant[] = rawTenants.map(tenant => ({
            id: tenant.id,
            fullName: tenant.fullName || '',
            phoneNumbers: tenant.phoneNumbers || [],
            emails: tenant.emails || [],
            nationality: tenant.nationality || null,
            identificationType: tenant.identificationType || '',
            identificationTypeId: tenant.identificationTypeId || '',
            username: tenant.username || '',
            active: tenant.active !== undefined ? tenant.active : true,
            files: tenant.files || []
          }));

          console.log('Raw tenants from API:', rawTenants);
          console.log('Mapped tenants:', tenants);

          // Filter by search term if provided
          let filteredTenants = tenants;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredTenants = tenants.filter(
              tenant =>
                (tenant.fullName && tenant.fullName.toLowerCase().includes(searchLower)) ||
                (tenant.username && tenant.username.toLowerCase().includes(searchLower)) ||
                (tenant.nationality && tenant.nationality.toLowerCase().includes(searchLower)) ||
                (tenant.identificationType && tenant.identificationType.toLowerCase().includes(searchLower)) ||
                tenant.phoneNumbers.some(phone => phone.toLowerCase().includes(searchLower)) ||
                tenant.emails.some(email => email.toLowerCase().includes(searchLower))
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredTenants.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof Tenant];
              let bValue: any = b[sortModel.field as keyof Tenant];

              // Handle special case for fullName sorting
              if (sortModel.field === 'fullName') {
                aValue = a.fullName.toLowerCase();
                bValue = b.fullName.toLowerCase();
              } else if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedTenants = filteredTenants.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredTenants.length / params.pageSize);

          const result = {
            items: paginatedTenants,
            totalCount: filteredTenants.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch tenants: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching tenants:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load tenants. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%', p: 1 }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Tenants Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Tenants"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchTenants}
        actions={actions}
        emptyMessage="No tenants found"
      />

      {/* Tenant Details Dialog */}
      <TenantDetailsDialog
        open={detailsDialogOpen}
        tenantId={selectedTenantId}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedTenantId(null);
        }}
        onRefresh={() => setRefreshKey((prev) => prev + 1)}
      />

      {/* Tenant Create Dialog */}
      <TenantCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Tenant Edit Dialog */}
      <TenantEditDialog
        open={editDialogOpen}
        tenant={selectedTenant}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTenant(null);
        }}
        onSuccess={handleEditSuccess}
      />
    </Box>
  );
};

export default TenantsListing;